import dayjs from "dayjs";

/**
 * Detect ISO date/datetime strings only. Avoids treating plain text with a capital "T"
 * (e.g. "Test Project") as dates — that produced invalid dayjs, "Invalid Date" in inputs,
 * and null on submit after prepareSubmitValues.
 */
function isIsoDateOrDateTimeString(value: string): boolean {
  const s = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {return true;}
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {return true;}
  return false;
}

/**
 * Values from the API (ISO date strings) → dayjs for Formily DatePicker fields.
 */
export function prepareInitialValues(
  data: Record<string, any>
): Record<string, any> {
  const updated = { ...data };
  for (const key of Object.keys(updated)) {
    const value = updated[key];
    if (typeof value === "string" && isIsoDateOrDateTimeString(value)) {
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        updated[key] = parsed;
      }
    }
  }
  return updated;
}

/**
 * Form values → API-safe payload: dayjs/Date → ISO, invalid dates and undefined → null
 * (avoids JSON `{}` for dayjs and omitted keys when fields are cleared).
 */
export function prepareSubmitValues(
  values: Record<string, any>
): Record<string, any> {
  const updated = { ...values };

  for (const key of Object.keys(updated)) {
    const value = updated[key];

    if (value === undefined) {
      updated[key] = null;
      continue;
    }
    if (value === null) {continue;}

    if (
      typeof value === "object" &&
      typeof (value as any).toISOString === "function"
    ) {
      if (
        typeof (value as any).isValid === "function" &&
        !(value as any).isValid()
      ) {
        updated[key] = null;
        continue;
      }
      updated[key] = (value as { toISOString: () => string }).toISOString();
    }
  }

  return updated;
}

/** Read `schema.properties` from a tenant schema JSON blob. */
export function getSchemaProperties(schemaJson: unknown): Record<string, any> {
  if (
    schemaJson &&
    typeof schemaJson === "object" &&
    "schema" in schemaJson &&
    typeof (schemaJson as { schema?: { properties?: unknown } }).schema ===
      "object"
  ) {
    const props = (schemaJson as { schema: { properties?: Record<string, any> } })
      .schema?.properties;
    return props && typeof props === "object" ? props : {};
  }
  return {};
}

/**
 * When a field is cleared, Formily may omit the key; many PATCH/PUT APIs keep the old
 * value unless the payload includes an explicit null. Only fills keys that exist in
 * the schema and were present on the loaded record.
 */
export function mergeClearedSchemaFieldsForUpdate(
  modified: Record<string, any>,
  originalCleaned: Record<string, any>,
  schemaProperties: Record<string, any>
): Record<string, any> {
  const out = { ...modified };
  for (const key of Object.keys(schemaProperties)) {
    if (!(key in out) && key in originalCleaned) {
      out[key] = null;
    }
  }
  return out;
}

/**
 * Every schema property key exists on the object (missing → null). Useful for symmetric
 * change detection vs. the server snapshot.
 */
export function normalizeMissingSchemaKeys(
  data: Record<string, any>,
  schemaProperties: Record<string, any>
): Record<string, any> {
  const normalized = { ...data };
  for (const key of Object.keys(schemaProperties)) {
    if (!(key in normalized)) {
      normalized[key] = null;
    }
  }
  return normalized;
}
